import * as DOMUtils from "history/DOMUtils";
import type { BuildAndBuyDispatchType, BuildAndBuyStateType } from "../redux/store";
import { NavStep } from "../../shared-logic/utils/Navigation";
import { getToyotaCode } from "../../../../common-deprecated/utils";
import { setModel } from "../redux/actions/ModelActions";
import { setGrade } from "../redux/actions/GradeActions";
import { finishConfigSave } from "../redux/actions/SaveActions";
import { initQuotes } from "../redux/actions/FinanceInsuranceActions";
import { initCar } from "../redux/actions/CarActions";
import type { FetchCarDataType } from "../../../shared-logic/server/data/FetchCar";
import type { FetchModelDataType } from "../server/data/FetchModel";
import type { FetchGradeType } from "../../../../common-deprecated/types/GradeType";
import { initCarItems } from "../redux/actions/CarItemActions";
import { setModelFiltersLoaded } from "../../../../common-deprecated/redux/actions/UtilActions";
import { setModelFilterData } from "../redux/actions/ModelFilterActions";
import { setSpecs } from "../redux/actions/SpecActions";
import { getConfigSpecsBody } from "../utils/techSpecUtils";
import { getTechSpecsAndEquipmentBody } from "../../../shared-logic/utils/techSpecUtils";
import {
    type FetchConfigurationSpecificationsBodyType,
    type FetchTechSpecAndEquipmentBodyType,
    PriceCalculationServiceEndpoint,
} from "../../../../common-deprecated/types/SpecTypes";
import type { FetchSpecsEquipmentDataType } from "../../../shared-logic/server/data/FetchSpecifications";
import { setSubModels } from "../redux/actions/SubmodelActions";
import { setGrades } from "../redux/actions/GradesActions";
import type { FetchGradesType } from "../../../../common-deprecated/types/GradesType";
import type { FetchModelFilterDataType } from "../server/data/FetchModelFilter";
import type { FetchFnReturnType } from "../../../../common-deprecated/utils/backendFetch";
import {
    initBnbSettings,
    setShowPageMonthlyRate as setBnbShowPageRate,
    setModelHasActiveFilters,
} from "../redux/actions/BnbSettingsActions";
import type { BnbSettingsType } from "../redux/reducers/BnbSettingsReducer";
import {
    enableExternalPriceCalculation,
    fetchIntegratedTradein,
    loadMassManagement as loadMassManagementFnc,
} from "../utils/settings/bnbSettingUtils";
import { modelIsLcv } from "../../../shared-logic/utils/lcvUtils";
import { finishMassManagementFetch } from "../redux/actions/MassManagementActions";
import {
    getModelMapValueById,
    hasAlternativeEcoSpecFlow,
    isLexus,
    searchModelMapValue,
} from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import { COMPONENT_PDF } from "../../../../shared-logic/server/components";
import type { TradeInAemExternalDataType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import {
    setTradeInAemExtData,
    setTradeInAemExtDataFailed,
} from "../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import type { FetchSubModelDataType } from "../server/data/FetchSubModels";
import {
    finishExternalPriceValueFetch,
    setExternalPriceError,
    setExternalPriceTaxLocation,
} from "../redux/actions/ExternalPriceActions";
import type { CO2SpecsType } from "../../../shared-logic/types/ConfigurationSpecificationsObjectType";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { ItemsType } from "../../../../common-deprecated/redux/typeHelpers";
import type { SavedConfigType } from "../../../shared-logic/types/SaveTypes";

import { maximizeBuildBasket } from "../utils/basketUtils";
import type { ExternalPriceCalulatedPriceType } from "../../../shared-logic/types/externalPriceTypes";
import {
    type FetchEcoConsumptionAceConfigType,
    type FetchEcoConsumptionCostsType,
    FETCH_ECO_CONSUMPTION_CONFIGURATION,
    FETCH_ECO_CONSUMPTION_COSTS,
    fetchEcoConsumptionAceConfigUtil,
    fetchEcoConsumptionCostsUtil,
} from "../../../shared-modules/eco-consumption-section/utils/ecoConsumptionConfigurationUtils";
import {
    initEcoConsumptionConfiguration,
    resetEcoConsumptionCosts,
    setEcoConsumptionCosts,
} from "../../../shared-modules/eco-consumption-section/redux/reducers/EcoConsumptionReducer";
import { FINANCE_TIMEOUT } from "../../../../common-deprecated/constants";
import { getRequestFailureStatus, isRequestPossible } from "../../../shared-logic/utils/abortedRequestUtils";
import type { FetchItemsType } from "../../../shared-logic/server/data/fetchItems";

// ----------------------------------------------------------------------
// Fetch types
// ----------------------------------------------------------------------

export const FETCH_CAR = "fetchCar";
export const FETCH_SAVED_CAR = "fetchSavedCar";
export const FETCH_CODE_CONFIGURATION = "fetchCodeConfig";
export const FETCH_MODEL = "fetchModel";
export const FETCH_SUB_MODELS = "fetchSubModel";
export const FETCH_GRADE = "fetchGrade";
export const FETCH_TRADE_IN_AEM_VALUATION = "fetchTradeInAemValuation";
export const FETCH_SPECS_EQUIPMENT = "fetchSpecsEquipment";
export const FETCH_GRADES_FOR_SUBMODEL = "fetchGradesForSubmodel";
export const FETCH_MODEL_FILTER = "fetchModelFilter";
export const FETCH_BNB_SETTINGS = "fetchBnbSettings";
export const FETCH_TMEF_DATA = "fetchTmefData";

type FetchCarDispatchType = {
    fetchType: typeof FETCH_CAR;
    modelId: string;
    carId: string;
    result?: FetchFnReturnType<FetchCarDataType & FetchItemsType>;
};
type FetchSavedCarDispatchType = {
    fetchType: typeof FETCH_SAVED_CAR;
    savedData: SavedConfigType;
    result?: FetchFnReturnType<FetchCarDataType & FetchItemsType>;
};
type FetchCodeConfigDispatchType = {
    fetchType: typeof FETCH_CODE_CONFIGURATION;
    result?: FetchFnReturnType<SavedConfigType>;
    tyCode: string;
};
type FetchModelDispatchType = {
    fetchType: typeof FETCH_MODEL;
    modelId: string;
    result?: FetchFnReturnType<FetchModelDataType>;
};
type FetchGradeDispatchType = {
    fetchType: typeof FETCH_GRADE;
    gradeId: string;
    bodyTypeId: string;
    modelId: string;
    result?: FetchFnReturnType<FetchGradeType>;
};
type FetchGradesForSubmodelDispatchType = {
    fetchType: typeof FETCH_GRADES_FOR_SUBMODEL;
    submodelId: string;
    modelId: string;
    modelCode: string;
    result?: FetchFnReturnType<FetchGradesType>;
};
type FetchTradeInAemValuationType = {
    fetchType: typeof FETCH_TRADE_IN_AEM_VALUATION;
    quoteId: string;
    result?: FetchFnReturnType<TradeInAemExternalDataType | null>;
};
type FetchSpecsEquipmentType = {
    fetchType: typeof FETCH_SPECS_EQUIPMENT;
    body: FetchTechSpecAndEquipmentBodyType;
    loadMassManagement: boolean;
    result?: FetchFnReturnType<FetchSpecsEquipmentDataType>;
};
export type FetchSubModelsType = {
    fetchType: typeof FETCH_SUB_MODELS;
    modelId: string;
    modelCode: string;
    result?: FetchFnReturnType<FetchSubModelDataType>;
};
type FetchModelFilterType = {
    fetchType: typeof FETCH_MODEL_FILTER;
    modelId: string;
    result?: FetchFnReturnType<FetchModelFilterDataType>;
};
export type FetchBnbSettingsType = {
    fetchType: typeof FETCH_BNB_SETTINGS;
    modelId?: string;
    result?: FetchFnReturnType<BnbSettingsType>;
};

type FetchTmefDataType = {
    fetchType: typeof FETCH_TMEF_DATA;
    payload: FetchConfigurationSpecificationsBodyType;
    car: CarType;
    result?: FetchFnReturnType<{ calculatedPrice: ExternalPriceCalulatedPriceType; co2Specs?: CO2SpecsType } | null>;
};

export const fetchCar = (modelId: string, carId: string): FetchCarDispatchType => ({
    fetchType: FETCH_CAR,
    carId,
    modelId,
});
export const fetchSavedCar = (savedData: SavedConfigType): FetchSavedCarDispatchType => ({
    fetchType: FETCH_SAVED_CAR,
    savedData,
});
export const fetchCodeConfig = (tyCode: string): FetchCodeConfigDispatchType => ({
    fetchType: FETCH_CODE_CONFIGURATION,
    tyCode,
});
export const fetchModel = (modelId: string): FetchModelDispatchType => ({ fetchType: FETCH_MODEL, modelId });
export const fetchSubModels = (modelId: string, modelCode: string): FetchSubModelsType => ({
    fetchType: FETCH_SUB_MODELS,
    modelId,
    modelCode,
});
export const fetchGrade = (gradeId: string, bodyTypeId: string, modelId: string): FetchGradeDispatchType => ({
    fetchType: FETCH_GRADE,
    gradeId,
    bodyTypeId,
    modelId,
});

export const fetchGradesForSubmodel = (
    submodelId: string,
    modelId: string,
    modelCode: string,
): FetchGradesForSubmodelDispatchType => ({
    fetchType: FETCH_GRADES_FOR_SUBMODEL,
    submodelId,
    modelId,
    modelCode,
});

export const fetchTradeInAemValuation = (quoteId: string): FetchTradeInAemValuationType => ({
    fetchType: FETCH_TRADE_IN_AEM_VALUATION,
    quoteId,
});
export const fetchSpecsEquipment = (
    body: FetchTechSpecAndEquipmentBodyType,
    loadMassManagement: boolean,
): FetchSpecsEquipmentType => ({
    fetchType: FETCH_SPECS_EQUIPMENT,
    loadMassManagement,
    body,
});
export const fetchModelFilter = (modelId: string): FetchModelFilterType => ({
    fetchType: FETCH_MODEL_FILTER,
    modelId,
});
export const fetchBnbSettings = (modelId?: string): FetchBnbSettingsType => ({
    fetchType: FETCH_BNB_SETTINGS,
    modelId,
});

export const fetchTmefData = (payload: FetchConfigurationSpecificationsBodyType, car: CarType): FetchTmefDataType => ({
    fetchType: FETCH_TMEF_DATA,
    payload,
    car,
});

export type BuildFetchTypes =
    | FetchModelFilterType // this type is re-used from the common fetching logic
    | FetchCarDispatchType
    | FetchSavedCarDispatchType
    | FetchCodeConfigDispatchType
    | FetchModelDispatchType
    | FetchGradeDispatchType
    | FetchTradeInAemValuationType
    | FetchSpecsEquipmentType
    | FetchSubModelsType
    | FetchGradesForSubmodelDispatchType
    | FetchBnbSettingsType
    | FetchTmefDataType
    | FetchEcoConsumptionAceConfigType
    | FetchEcoConsumptionCostsType;

// ----------------------------------------------------------------------
// Fetch getter
// ----------------------------------------------------------------------
export const getBuildComponentFetches = (state: BuildAndBuyStateType): BuildFetchTypes[] => {
    const fetches: BuildFetchTypes[] = [];
    const { navigation, bnbSettings, commonSettings, tradeInAemExternalData, externalPrice, car, ecoConsumption } =
        state;
    const { step } = navigation;
    const { query } = commonSettings;

    const isPdf = commonSettings.component === COMPONENT_PDF;

    // Fetch settings first.
    if (!bnbSettings.initialized) {
        fetches.push(fetchBnbSettings(navigation.model));
        return fetches;
    }

    // ----------------------------------------------------------------------
    // Steps before customize
    // ----------------------------------------------------------------------

    // (sub)Model step data
    if ((step === NavStep.Model || step === NavStep.Submodels) && navigation.model) {
        // Fallback logic to retrieve a valid modelId anyway even if "yaris" is sent.
        // modelHasNoFilters relates to the proace filters being downloaded but for some reason no filters were present.
        const modelHasNoFilters =
            modelIsLcv(navigation.model) && !bnbSettings.modelHasActiveFilters && state.utils.modelFiltersLoaded;
        const modelId = searchModelMapValue(commonSettings, navigation.model)?.id;

        if (modelId && (!modelIsLcv(modelId) || modelHasNoFilters) && state.model.id !== modelId) {
            // Send the model in case we didn't find a modelId. This will propagate a GUID error from Texus.
            fetches.push(fetchModel(modelId));
        }

        if (modelId && isLexus(commonSettings) && step === NavStep.Submodels && !state.submodels.initialized) {
            const modelMapValue = getModelMapValueById(commonSettings, modelId, false);
            fetches.push(fetchSubModels(modelId, modelMapValue?.internalCode || ""));
        }

        if (modelId && modelIsLcv(modelId) && !state.utils.modelFiltersLoaded) {
            fetches.push(fetchModel(modelId));
            fetches.push(fetchModelFilter(modelId));
        }
    }

    // Grades step data
    if (step === NavStep.Grades && !state.grades.initialized) {
        const modelId = searchModelMapValue(commonSettings, navigation.model)?.id || "";
        const modelMapValue = getModelMapValueById(commonSettings, modelId, false);
        fetches.push(fetchGradesForSubmodel(navigation.submodel || "", modelId, modelMapValue?.internalCode || ""));
    }

    // Engine step data
    if (step === NavStep.Engine && state.grade.id !== navigation.grade) {
        fetches.push(fetchGrade(navigation.grade || "", navigation.bodyType || "", navigation.model || ""));
    }

    // ----------------------------------------------------------------------
    // Data for Customize step onwards
    // ----------------------------------------------------------------------

    // Car and TyCode data.
    if ([NavStep.Customize, NavStep.Services, NavStep.Summary, NavStep.Buy].includes(step) || isPdf) {
        // First check if we have a tycode because if we do this will become the configuration we load.
        const tyCode = getToyotaCode(query);
        if (tyCode) {
            if (!state.save.data) {
                // TyCode found but data not fetched data yet.
                fetches.push(fetchCodeConfig(getToyotaCode(commonSettings.query)));
            } else if (state.save.data.CarID !== state.car.id) {
                // TyCode found and data present. Propagate save data to fetch method.
                fetches.push(fetchSavedCar(state.save.data));
            }
        } else if (state.car.id !== navigation.car) {
            // No tyCode, fetch based on model and car id.
            fetches.push(fetchCar(navigation.model || "", navigation.car || ""));
        }
    }

    // Fetch specifications.
    const loadMassMgmt = loadMassManagementFnc(bnbSettings);
    if (
        (step === NavStep.Summary || ([NavStep.Customize, NavStep.Services].includes(step) && loadMassMgmt)) &&
        state.car.id &&
        state.items.initialized &&
        !state.specs.fetched
    ) {
        // During the first fetch the basket is only initialized in postFetch but this fetch already requires it.
        // If the basket is not available yet use the maximizeBasket function to retrieve a basket object.
        const basket = state.basket.initialized
            ? state.basket
            : maximizeBuildBasket(state.car, state.items, state.basket, commonSettings.query);

        const body = getTechSpecsAndEquipmentBody(state.car, basket, state.items, state.utils.financeOption);
        fetches.push(fetchSpecsEquipment(body, loadMassMgmt));
    }

    if (hasAlternativeEcoSpecFlow(commonSettings) && car.id) {
        const modelId = searchModelMapValue(commonSettings, navigation.model)?.id || "";
        const modelMapValue = getModelMapValueById(commonSettings, modelId, false);
        // Normally speaking we always have a modelId in this scenario but typing allows null
        if (!ecoConsumption.initialized && modelMapValue) {
            fetches.push(fetchEcoConsumptionAceConfigUtil(modelMapValue.internalCode));
        }
        if (bnbSettings.initialized && isRequestPossible(ecoConsumption.costsStatus)) {
            const config = getConfigSpecsBody(state, true, PriceCalculationServiceEndpoint.Prices);
            if (config) {
                const timeout = !DOMUtils.canUseDOM && !isPdf ? FINANCE_TIMEOUT : undefined;
                fetches.push(fetchEcoConsumptionCostsUtil(bnbSettings.ecoConsumptionCostService, config, timeout));
            }
        }
    }

    // Integrated Trade-in
    // We can assume that the quoteId which is extracted from the tyCode is always valid. An invalid tyCode with incompatible trade-in / finance should never be generated frontend wise.
    if (
        fetchIntegratedTradein(bnbSettings, commonSettings) &&
        ([NavStep.Customize, NavStep.Services, NavStep.Summary].includes(step) || isPdf)
    ) {
        const quoteId = query.quoteId || state.save?.data?.Personal?.Parameters?.TradeInQuoteID;
        if (quoteId && !tradeInAemExternalData.failed && !tradeInAemExternalData.data.vehicleValue) {
            fetches.push(fetchTradeInAemValuation(quoteId));
        }
    }

    if (!isPdf) return fetches;

    // ----------------------------------------------------------------------
    // PDF fetches
    // ----------------------------------------------------------------------

    if (state.car.id && state.items.initialized) {
        // // During the first fetch the basket is only initialized in postFetch but this fetch already requires it.
        // // If the basket is not available yet use the maximizeBasket function to retrieve a basket object.
        const basket = state.basket.initialized
            ? state.basket
            : maximizeBuildBasket(state.car, state.items, state.basket, commonSettings.query);

        const enableExternalPriceSetting =
            enableExternalPriceCalculation(commonSettings, bnbSettings, car) && bnbSettings.priceCalculationServiceAPI; // If the server side externalPriceCalculation is not set don't enable external price for PDf as this does not work in SSR otherwise

        if (enableExternalPriceSetting && !externalPrice.error && !externalPrice.calculatedPrice.carPrice) {
            const body = getConfigSpecsBody({ ...state, basket }, true);

            fetches.push(fetchTmefData(body, car));
        }
    }

    return fetches;
};

// ----------------------------------------------------------------------
// Dispatcher
// ----------------------------------------------------------------------
export const buildFetchDispatcher = (data: Required<BuildFetchTypes>, dispatch: BuildAndBuyDispatchType): void => {
    // ❗ Warning: when refactoring, make sure that type inference still works. For example:
    // - Make sure to always use the data constant instead of destructuring
    // - Don't use [].includes(), use switches instead

    switch (data.fetchType) {
        case FETCH_BNB_SETTINGS: {
            if (data.result) dispatch(initBnbSettings(data.result.fetchResult));
            break;
        }
        case FETCH_TMEF_DATA: {
            if (data.result.errorLogs?.length) {
                dispatch(setExternalPriceError(true));
            } else if (data.result.fetchResult?.calculatedPrice) {
                dispatch(
                    finishExternalPriceValueFetch(
                        data.result.fetchResult.calculatedPrice,
                        data.result.fetchResult.co2Specs || null,
                    ),
                );
            }
            break;
        }
        case FETCH_SUB_MODELS: {
            dispatch(setSubModels(data.result.fetchResult.submodels));
            break;
        }
        case FETCH_GRADES_FOR_SUBMODEL: {
            dispatch(setGrades(data.result.fetchResult));
            break;
        }
        case FETCH_MODEL: {
            dispatch(setModel(data.result.fetchResult.model));
            break;
        }
        case FETCH_GRADE: {
            dispatch(setGrade(data.result.fetchResult.grade));
            break;
        }
        case FETCH_CODE_CONFIGURATION: {
            dispatch(finishConfigSave(data.result.fetchResult));
            if (data.result.fetchResult && data.result.fetchResult.Personal) {
                const { FinanceQuoteID, InsuranceQuoteID } = data.result.fetchResult.Personal.Parameters;

                // When porting this functionality from USC to NC, we made a typo (lowercase c)
                // For backwards compatibility we still try to read out the old value first
                const UserDepartmentCode =
                    (data.result.fetchResult.Personal.Parameters as any).UserDepartmentcode ||
                    data.result.fetchResult.Personal.Parameters.UserDepartmentCode;

                if (UserDepartmentCode) dispatch(setExternalPriceTaxLocation(UserDepartmentCode));
                if (FinanceQuoteID || InsuranceQuoteID) {
                    dispatch(initQuotes(FinanceQuoteID, InsuranceQuoteID));
                }
            }
            break;
        }
        case FETCH_TRADE_IN_AEM_VALUATION: {
            if (data.result.fetchResult) dispatch(setTradeInAemExtData(data.result.fetchResult));
            else dispatch(setTradeInAemExtDataFailed(true));
            break;
        }
        case FETCH_CAR:
        case FETCH_SAVED_CAR: {
            dispatch(initCar(data.result.fetchResult.car));
            break;
        }

        case FETCH_MODEL_FILTER: {
            const { name, internalCode, filterInfo } = data.result.fetchResult;

            if (filterInfo.filters.length) dispatch(setModelHasActiveFilters(true));

            // This is a separate flag because it is possible no filters are published.
            dispatch(setModelFiltersLoaded(true));
            dispatch(setModelFilterData(name, internalCode, filterInfo.cars, filterInfo.filters));
            break;
        }

        case FETCH_SPECS_EQUIPMENT: {
            const { fetchResult } = data.result;
            if ((data as FetchSpecsEquipmentType).loadMassManagement) {
                const { exceedingKilogram, error } = fetchResult.massValidationSpecs;
                dispatch(finishMassManagementFetch(exceedingKilogram, !!error));
            }
            dispatch(setSpecs(fetchResult.techSpecs, fetchResult.standardEquipment));
            break;
        }

        case FETCH_ECO_CONSUMPTION_CONFIGURATION: {
            const { result } = data;
            const { labels, ace } = result?.fetchResult || {
                labels: null,
                ace: null,
            };
            dispatch(
                initEcoConsumptionConfiguration({
                    labels,
                    ace,
                }),
            );
            break;
        }

        case FETCH_ECO_CONSUMPTION_COSTS: {
            const { result } = data;
            if (result.fetchResult) {
                dispatch(setEcoConsumptionCosts({ costs: result.fetchResult }));
            } else {
                dispatch(resetEcoConsumptionCosts(getRequestFailureStatus(result)));
            }
            break;
        }
    }

    // TODO: check why FETCH_SUB_MODELS was here (https://github.com/marlon-be/toyota-online-retailer/pull/4714)
    switch (data.fetchType) {
        case FETCH_MODEL:
        case FETCH_GRADE:
        case FETCH_SAVED_CAR:
        case FETCH_GRADES_FOR_SUBMODEL: {
            dispatch(initCarItems(data.result.fetchResult.items as ItemsType));
            break;
        }

        case FETCH_CAR: {
            dispatch(initCarItems(data.result.fetchResult.items));
            break;
        }
    }

    switch (data.fetchType) {
        case FETCH_MODEL:
        case FETCH_GRADE:
        case FETCH_SUB_MODELS:
        case FETCH_GRADES_FOR_SUBMODEL: {
            dispatch(setBnbShowPageRate(data.result.fetchResult.showMonthlyRates));
        }
    }
};
